import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { toast } from "react-toastify";

import {
  getVisitorAdminSummary,
} from "../../../../../services/visitorTrackingServices";

export const EMPTY_VISITOR_ADMIN_SUMMARY = {
  live_visitors: 0,
  today_sessions: 0,

  unread_conversations: 0,
  unread_messages: 0,
  open_conversations: 0,

  new_callback_requests: 0,

  total_alerts: 0,
  generated_at: null,
};

function safeNumber(value) {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
}

export default function useVisitorAdminAlerts({
  enabled = true,
  pollMs = 8000,
} = {}) {
  const [summary, setSummary] = useState(
    EMPTY_VISITOR_ADMIN_SUMMARY,
  );

  const [loading, setLoading] = useState(false);

  const [errorMessage, setErrorMessage] =
    useState("");

  const previousSummaryRef = useRef(null);
  const requestRunningRef = useRef(false);

  /**
   * Affiche les notifications lorsqu'une valeur augmente.
   *
   * La première récupération ne déclenche aucune notification :
   * elle sert uniquement de valeur de référence.
   */
  const notifyFromChanges = useCallback(
    (previousSummary, nextSummary) => {
      if (!previousSummary) {
        return;
      }

      const newConversationCount =
        safeNumber(
          nextSummary.unread_conversations,
        ) -
        safeNumber(
          previousSummary.unread_conversations,
        );

      const newMessageCount =
        safeNumber(nextSummary.unread_messages) -
        safeNumber(previousSummary.unread_messages);

      const newCallbackCount =
        safeNumber(
          nextSummary.new_callback_requests,
        ) -
        safeNumber(
          previousSummary.new_callback_requests,
        );

      const newVisitorCount =
        safeNumber(nextSummary.live_visitors) -
        safeNumber(previousSummary.live_visitors);

      /*
       * Nouvelle conversation.
       */
      if (newConversationCount > 0) {
        toast.info(
          newConversationCount === 1
            ? "Une nouvelle conversation vient d'être créée."
            : `${newConversationCount} nouvelles conversations viennent d'être créées.`,
          {
            toastId: `visitor-conversation-${Date.now()}`,
            autoClose: 6000,
          },
        );
      }
      /*
       * Nouveau message dans une conversation existante.
       *
       * On utilise else if pour éviter deux notifications
       * lorsqu'une nouvelle conversation contient son premier message.
       */
      else if (newMessageCount > 0) {
        toast.info(
          newMessageCount === 1
            ? "Vous avez reçu un nouveau message."
            : `Vous avez reçu ${newMessageCount} nouveaux messages.`,
          {
            toastId: `visitor-message-${Date.now()}`,
            autoClose: 6000,
          },
        );
      }

      /*
       * Nouvelle demande de rappel ou de démonstration.
       */
      if (newCallbackCount > 0) {
        toast.success(
          newCallbackCount === 1
            ? "Une nouvelle demande de rappel vient d'être reçue."
            : `${newCallbackCount} nouvelles demandes de rappel viennent d'être reçues.`,
          {
            toastId: `visitor-callback-${Date.now()}`,
            autoClose: 7000,
          },
        );
      }

      /*
       * Nouveau visiteur détecté sur le site.
       */
      if (newVisitorCount > 0) {
        toast.info(
          newVisitorCount === 1
            ? "Un nouveau visiteur vient d'arriver sur le site."
            : `${newVisitorCount} nouveaux visiteurs viennent d'arriver sur le site.`,
          {
            toastId: `visitor-arrival-${Date.now()}`,
            autoClose: 3500,
          },
        );
      }
    },
    [],
  );

  const refresh = useCallback(
    async ({
      silent = false,
      notify = true,
    } = {}) => {
      if (!enabled || requestRunningRef.current) {
        return null;
      }

      try {
        requestRunningRef.current = true;

        if (!silent) {
          setLoading(true);
        }

        setErrorMessage("");

        const response =
          await getVisitorAdminSummary();

        const nextSummary = {
          ...EMPTY_VISITOR_ADMIN_SUMMARY,
          ...(response?.data || {}),
        };

        if (notify) {
          notifyFromChanges(
            previousSummaryRef.current,
            nextSummary,
          );
        }

        /*
         * La valeur précédente est mise à jour après
         * la comparaison.
         */
        previousSummaryRef.current = nextSummary;

        setSummary(nextSummary);

        return nextSummary;
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Impossible de récupérer les alertes visiteurs.";

        setErrorMessage(message);

        return null;
      } finally {
        requestRunningRef.current = false;

        if (!silent) {
          setLoading(false);
        }
      }
    },
    [enabled, notifyFromChanges],
  );

  /**
   * Chargement initial et polling.
   */
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    /*
     * La première récupération initialise les compteurs,
     * sans produire de notification.
     */
    refresh({
      notify: false,
    });

    const intervalId = window.setInterval(() => {
      refresh({
        silent: true,
        notify: true,
      });
    }, pollMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [enabled, pollMs, refresh]);

  /**
   * Mise à jour immédiate lorsque l'utilisateur revient
   * sur l'onglet ou la fenêtre du navigateur.
   */
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleWindowFocus = () => {
      refresh({
        silent: true,
        notify: true,
      });
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible"
      ) {
        refresh({
          silent: true,
          notify: true,
        });
      }
    };

    window.addEventListener(
      "focus",
      handleWindowFocus,
    );

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange,
    );

    return () => {
      window.removeEventListener(
        "focus",
        handleWindowFocus,
      );

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      );
    };
  }, [enabled, refresh]);

  /**
   * Remise à zéro lors de la déconnexion,
   * du changement de rôle ou de la sortie de /appli.
   */
  useEffect(() => {
    if (enabled) {
      return;
    }

    previousSummaryRef.current = null;
    requestRunningRef.current = false;

    setSummary(EMPTY_VISITOR_ADMIN_SUMMARY);
    setLoading(false);
    setErrorMessage("");
  }, [enabled]);

  return {
    summary,
    loading,
    errorMessage,
    refresh,
  };
}